import { IconArrowsHorizontal, IconCurrencyDollar } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useEffect } from 'react';
import './Expenses.scss';
import { NSDropdown, NSInput, NSLabel } from 'bricks';
import { ProFormaModelIds } from 'constants/proForma';
import classNames from 'classnames';
import { IExpense } from 'views/ProFormaSetup/hooks/useExpenses';
import { COST_TYPES, IExpenses } from 'views/ProFormaSetup/types';
import ExpensesDetail from '../ExpensesDetail/ExpensesDetail';

export interface IExpensesProps {
    values: IExpenses;
    proFormaModelId: number;
    totalIncomeUnits?: number;
    totalSFPerUnits?: number;
    onChange: (key: string, value: number | string) => void;
    onDetailChange: (updatedExpenses: IExpense[]) => void;
    initialExpenses: IExpense[];
    showExpenseDetail: boolean;
    setShowExpenseDetail: (showExpenseDetail: boolean) => void;
}

const Expenses = ({
    values, proFormaModelId, totalIncomeUnits, totalSFPerUnits, onChange, onDetailChange, initialExpenses, showExpenseDetail, setShowExpenseDetail,
}: IExpensesProps) => {
    const filteredUnits = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)
        ? COST_TYPES
        : COST_TYPES.filter(unit => unit.value === 'sf');

    const updateCosts = (
        type: 'expenses' | 'totalExpenses',
        newValue: number,
        relatedType: 'expenses' | 'totalExpenses',
        multiplierType: 'expensesType',
    ) => {
        const multiplier = values[multiplierType] === 'units' ? totalIncomeUnits : totalSFPerUnits;
        if (type.includes('total')) {
            onChange(type, newValue);
            onChange(relatedType, newValue / (multiplier ?? 1));
        } else {
            onChange(type, newValue);
            onChange(relatedType, newValue * (multiplier ?? 1));
        }
    };

    useEffect(() => {
        updateCosts('expenses', values.expenses, 'totalExpenses', 'expensesType');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.expensesType]);

    return (
        <div className="w-100">
            <div className="Expenses__title">Expenses</div>
            <div className="Expenses__subtitle">Overview of this investment’s total expenses</div>
            <div
                className={classNames([
                    'd-flex flex-row align-items-center w-100 pb-3',
                    {
                        'Expenses__inputs-container--disabled': showExpenseDetail,
                    },
                ])}
            >
                <NSLabel className="text-dark font-weight-normal pt-1 mr-4" htmlFor="hardCosts">
                    Expenses
                </NSLabel>
                <NSInput
                    id="expenses"
                    dataTestId="expenses"
                    type="number"
                    name="expenses"
                    placeholder="Enter expenses"
                    disabled={showExpenseDetail}
                    prependInputAddon={<NSIcon icon={IconCurrencyDollar} />}
                    value={values.expenses}
                    onChange={(e: { target: { value: number } }) => updateCosts('expenses', Number(e.target.value), 'totalExpenses', 'expensesType')}
                    appendInputAddon={(
                        <NSDropdown
                            options={filteredUnits}
                            selectedOption={filteredUnits.find(option => option.value === values.expensesType)}
                            customSelectedOptionText={filteredUnits.find(option => option.value === values.expensesType)?.label}
                            handleSelectOption={option => onChange('expensesType', option.value)}
                            badgeColor=""
                            isBadgeToggle
                            toggleClassName="p-0 m-0"
                        />
                    )}
                />
                <NSIcon icon={IconArrowsHorizontal} className="mx-2" />
                <NSInput
                    id="totalHardCosts"
                    dataTestId="total-expenses"
                    type="number"
                    name="totalHardCosts"
                    disabled={showExpenseDetail}
                    value={values.amountPerYear}
                    onChange={(e: { target: { value: number } }) => updateCosts('totalExpenses', Number(e.target.value), 'expenses', 'expensesType')}
                    prependInputAddon={<NSIcon icon={IconCurrencyDollar} />}
                    appendInputAddon={<div>Total</div>}
                />
            </div>
            <ExpensesDetail
                initialValues={initialExpenses}
                proFormaModelId={proFormaModelId}
                showExpenseDetail={showExpenseDetail}
                setShowExpenseDetail={setShowExpenseDetail}
                onChange={onDetailChange}
            />
        </div>
    );
};

export default Expenses;
